/**
 * userContactEdits — how an edit on a detail-page contact row becomes the next
 * `connection_views.user_contacts` value.
 *
 * Pulled out of ProfileConnectionDetailPage so the sequence is testable: it is
 * applied TWICE in one tick when ContactFields promotes phone2 → phone on a
 * clear, and the second application must build on the first. Doing this inline
 * against the render closure silently dropped the promotion and deleted the
 * promoted value (found at /go:review, 2026-08-19).
 */

/**
 * Apply one field change.
 *
 * @param {object} current - the user_contacts value being edited
 * @param {string} fieldKey - e.g. 'phone', 'email2'
 * @param {string} value - the new value; empty means cleared
 * @param {object} opts
 * @param {object} opts.peerShared - the peer's declared fields, by key
 * @param {boolean} opts.peerHasClaimed - has the peer claimed their fields
 *   (isActiveRoyalePeer)? Claimed fields aren't editable, so this only shapes
 *   how a clear is recorded.
 * @returns {object} the next user_contacts value
 */
export const applyUserContactChange = (
  current,
  fieldKey,
  value,
  { peerShared = {}, peerHasClaimed = false } = {}
) => {
  const next = { ...(current || {}) };

  if (value) {
    next[fieldKey] = value;
    return next;
  }

  // Clearing a value the peer declared has to persist as an empty override:
  // deleting the key would let peerShared surface again on the next render, so
  // the X would appear to do nothing.
  if (!peerHasClaimed && peerShared[fieldKey]) {
    next[fieldKey] = '';
    return next;
  }

  delete next[fieldKey];
  return next;
};
